import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { FaUsersCog, FaPlus, FaTrashAlt, FaEdit, FaUserCheck, FaSearch } from 'react-icons/fa';

interface MemberItem {
  id: number;
  name: string;
  email: string;
  company_name: string;
}

interface CommitteeMember {
  cm_id: number;
  user_id: number;
  name: string;
  email: string;
  company_name: string;
  role_in_committee: string;
}

interface CommitteeItem {
  id: number;
  name: string;
  description: string;
  created_at?: string;
  members: CommitteeMember[];
}

const CommitteeManagement: React.FC = () => {
  const [committees, setCommittees] = useState<CommitteeItem[]>([]);
  const [allUsers, setAllUsers] = useState<MemberItem[]>([]);
  const [loading, setLoading] = useState(true);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [committeeName, setCommitteeName] = useState('');
  const [description, setDescription] = useState('');
  const [selectedMemberIds, setSelectedMemberIds] = useState<number[]>([]);
  const [memberSearch, setMemberSearch] = useState('');

  const fetchData = async () => {
    try {
      const [commRes, userRes] = await Promise.all([
        axios.get<CommitteeItem[]>('https://rmbbackend.rmbjaipur.com/auth/committees'),
        axios.get<MemberItem[]>('https://rmbbackend.rmbjaipur.com/auth/allusersinfo')
      ]);
      setCommittees(commRes.data);
      setAllUsers(userRes.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching committees data:', error);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const openCreateModal = () => {
    setEditingId(null);
    setCommitteeName('');
    setDescription('');
    setSelectedMemberIds([]);
    setIsModalOpen(true);
  };

  const openEditModal = (committee: CommitteeItem) => {
    setEditingId(committee.id);
    setCommitteeName(committee.name);
    setDescription(committee.description || '');
    setSelectedMemberIds(committee.members.map(m => m.user_id));
    setIsModalOpen(true);
  };

  const toggleMemberSelection = (userId: number) => {
    if (selectedMemberIds.includes(userId)) {
      setSelectedMemberIds(prev => prev.filter(id => id !== userId));
    } else {
      setSelectedMemberIds(prev => [...prev, userId]);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!committeeName.trim()) {
      alert('Please enter a committee name');
      return;
    }

    try {
      if (editingId) {
        await axios.put(`https://rmbbackend.rmbjaipur.com/auth/committees/${editingId}`, {
          name: committeeName,
          description,
          member_ids: selectedMemberIds
        });
        alert('Committee updated successfully!');
      } else {
        await axios.post('https://rmbbackend.rmbjaipur.com/auth/committees', {
          name: committeeName,
          description,
          member_ids: selectedMemberIds
        });
        alert('Committee created successfully!');
      }
      setIsModalOpen(false);
      fetchData();
    } catch (error) {
      console.error('Error saving committee:', error);
      alert('Failed to save committee.');
    }
  };

  const handleDeleteCommittee = async (id: number) => {
    if (window.confirm('Are you sure you want to delete this committee?')) {
      try {
        await axios.delete(`https://rmbbackend.rmbjaipur.com/auth/committees/${id}`);
        setCommittees(prev => prev.filter(c => c.id !== id));
      } catch (error) {
        console.error('Error deleting committee:', error);
        alert('Failed to delete committee.');
      }
    }
  };

  const filteredUsers = allUsers.filter(u =>
    u.name.toLowerCase().includes(memberSearch.toLowerCase()) ||
    (u.company_name && u.company_name.toLowerCase().includes(memberSearch.toLowerCase()))
  );

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="flex justify-between items-center bg-white p-5 rounded-xl shadow-sm border border-gray-200 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
            <FaUsersCog className="text-[#0A2451]" /> Committee Management
          </h1>
          <p className="text-sm text-gray-500">Create specialized committees (Event Planning, Membership, Finance) and assign multiple members.</p>
        </div>
        <button
          onClick={openCreateModal}
          className="flex items-center gap-2 bg-[#0A2451] hover:bg-blue-900 text-white font-bold px-4 py-2.5 rounded-lg shadow transition"
        >
          <FaPlus /> Create New Committee
        </button>
      </div>

      {/* Committee Cards List */}
      {loading ? (
        <p className="text-sm text-gray-500 text-center py-8">Loading committees...</p>
      ) : committees.length === 0 ? (
        <div className="bg-white p-8 rounded-xl text-center border border-gray-200">
          <FaUsersCog size={48} className="text-gray-300 mx-auto mb-3" />
          <h3 className="text-lg font-bold text-gray-700">No Committees Formed Yet</h3>
          <p className="text-sm text-gray-500 mb-4">Click "Create New Committee" above to start forming committees and adding members.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {committees.map(committee => (
            <div key={committee.id} className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden flex flex-col justify-between hover:shadow-md transition">
              <div className="p-5 border-b border-gray-100">
                <div className="flex justify-between items-start mb-2">
                  <h3 className="text-lg font-bold text-[#0A2451]">{committee.name}</h3>
                  <span className="bg-blue-50 text-blue-800 text-xs font-bold px-2.5 py-0.5 rounded-full border border-blue-200">
                    {committee.members.length} Members
                  </span>
                </div>
                {committee.description && (
                  <p className="text-xs text-gray-600 mb-3">{committee.description}</p>
                )}

                {/* Assigned Member List */}
                <div className="mt-3">
                  <span className="text-[11px] font-bold text-gray-500 uppercase block mb-2">Committee Members:</span>
                  {committee.members.length === 0 ? (
                    <span className="text-xs text-gray-400 italic">No members assigned yet.</span>
                  ) : (
                    <div className="flex flex-wrap gap-1.5 max-h-36 overflow-y-auto">
                      {committee.members.map(m => (
                        <span key={m.user_id} className="bg-gray-100 text-gray-800 text-xs font-medium px-2 py-0.5 rounded border border-gray-300 flex items-center gap-1">
                          <FaUserCheck className="text-green-600 text-[10px]" /> {m.name}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Action Buttons */}
              <div className="bg-gray-50 px-5 py-3 flex justify-end gap-2 border-t border-gray-100">
                <button
                  onClick={() => openEditModal(committee)}
                  className="px-3 py-1.5 text-xs font-bold text-blue-700 bg-blue-50 hover:bg-blue-100 rounded border border-blue-200 flex items-center gap-1"
                >
                  <FaEdit /> Edit & Manage Members
                </button>
                <button
                  onClick={() => handleDeleteCommittee(committee.id)}
                  className="px-3 py-1.5 text-xs font-bold text-red-600 bg-red-50 hover:bg-red-100 rounded border border-red-200 flex items-center gap-1"
                >
                  <FaTrashAlt /> Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal for Creating / Editing Committee */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden">
            <div className="bg-[#0A2451] px-6 py-4 text-white flex justify-between items-center">
              <h2 className="text-lg font-bold">
                {editingId ? 'Edit Committee & Assign Members' : 'Create New Committee'}
              </h2>
              <button
                onClick={() => setIsModalOpen(false)}
                className="text-white hover:text-yellow-400 text-xl font-bold"
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 overflow-y-auto space-y-4 flex-1">
              <div>
                <label className="block text-xs font-bold text-gray-700 uppercase mb-1">Committee Name *</label>
                <input
                  type="text"
                  placeholder="e.g. Event Planning Committee"
                  value={committeeName}
                  onChange={(e) => setCommitteeName(e.target.value)}
                  className="w-full p-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                  required
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-gray-700 uppercase mb-1">Description</label>
                <textarea
                  rows={2}
                  placeholder="Brief purpose of this committee..."
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  className="w-full p-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                />
              </div>

              {/* Multi-Member Selection */}
              <div>
                <div className="flex justify-between items-center mb-2">
                  <label className="text-xs font-bold text-gray-700 uppercase">
                    Select Committee Members ({selectedMemberIds.length} Selected)
                  </label>
                  <div className="relative w-48">
                    <input
                      type="text"
                      placeholder="Search member..."
                      value={memberSearch}
                      onChange={(e) => setMemberSearch(e.target.value)}
                      className="w-full pl-7 pr-2 py-1 text-xs border rounded-lg"
                    />
                    <FaSearch className="absolute left-2 top-2 text-gray-400 text-xs" />
                  </div>
                </div>

                <div className="border border-gray-200 rounded-xl p-3 max-h-56 overflow-y-auto bg-gray-50 space-y-2">
                  {filteredUsers.length === 0 ? (
                    <p className="text-xs text-gray-400 text-center py-4">No members found.</p>
                  ) : (
                    filteredUsers.map(user => {
                      const isSelected = selectedMemberIds.includes(user.id);
                      return (
                        <div
                          key={user.id}
                          onClick={() => toggleMemberSelection(user.id)}
                          className={`flex items-center justify-between p-2.5 rounded-lg border cursor-pointer transition select-none ${isSelected ? 'bg-blue-50 border-blue-400 shadow-sm' : 'bg-white border-gray-200 hover:bg-gray-100'
                            }`}
                        >
                          <div>
                            <span className="text-xs font-bold text-gray-800 block">{user.name}</span>
                            <span className="text-[11px] text-gray-500 block">{user.company_name || 'RMB Member'}</span>
                          </div>
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => { }} // Handled by parent div
                            className="h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                          />
                        </div>
                      );
                    })
                  )}
                </div>
              </div>

              <div className="pt-4 border-t flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  className="px-4 py-2 border border-gray-300 text-gray-700 text-sm font-semibold rounded-lg hover:bg-gray-100"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-5 py-2 bg-[#0A2451] hover:bg-blue-900 text-white text-sm font-bold rounded-lg shadow"
                >
                  {editingId ? 'Save Changes' : 'Create Committee'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default CommitteeManagement;
